import { useState, useEffect } from "react";
import { fetchMerchants, renameMerchant, deleteMerchant } from "../api";
import { FONT, COLORS, GRADIENT, FontLink } from "../styles";
import UserManagement from "./UserManagement";

export default function MerchantSelectScreen({ onSelect, onLogout, loading, statusMsg }) {
  const [merchants, setMerchants] = useState([]);
  const [selected, setSelected] = useState("");
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [showUsers, setShowUsers] = useState(false);

  const loadMerchants = () => {
    setFetching(true);
    fetchMerchants()
      .then((m) => { setMerchants(m); setFetching(false); })
      .catch((err) => { setError(err.message || "Cannot reach backend"); setFetching(false); });
  };

  useEffect(() => { loadMerchants(); }, []);

  const handleRename = async (merchantId) => {
    if (!editName.trim()) return;
    setActionLoading(true);
    try { await renameMerchant(merchantId, editName.trim()); setEditingId(null); loadMerchants(); }
    catch (err) { setError(err.message); }
    finally { setActionLoading(false); }
  };

  const handleDelete = async (merchantId, merchantName) => {
    if (!window.confirm(`Delete "${merchantName}"? This cannot be undone.`)) return;
    setActionLoading(true);
    try { await deleteMerchant(merchantId); if (selected === merchantId) setSelected(""); loadMerchants(); }
    catch (err) { setError(err.message); }
    finally { setActionLoading(false); }
  };

  return (
    <div style={{ minHeight: "100vh", background: COLORS.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: FONT }}>
      <FontLink />
      {showUsers && <UserManagement onClose={() => setShowUsers(false)} />}
      <div style={{ textAlign: "center", maxWidth: 560, padding: 40, width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginBottom: 20 }}>
          <button onClick={() => setShowUsers(true)} style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "7px 16px", fontSize: 12, color: COLORS.textSecondary, cursor: "pointer", fontFamily: FONT, fontWeight: 500 }}>Users</button>
          <button onClick={onLogout} style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "7px 16px", fontSize: 12, color: COLORS.red, cursor: "pointer", fontFamily: FONT, fontWeight: 500 }}>Sign Out</button>
        </div>
        <div style={{ width: 56, height: 56, background: GRADIENT, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", boxShadow: "0 4px 14px rgba(37,99,235,0.25)" }}>
          <span style={{ fontSize: 24, fontWeight: 800, color: "#fff" }}>S</span>
        </div>
        <h1 style={{ color: COLORS.text, fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Bank Analysis</h1>
        <p style={{ color: COLORS.muted, fontSize: 14, marginBottom: 32 }}>Select a merchant to load their analysis</p>

        {error && (
          <div style={{ background: COLORS.redLight, border: "1px solid #FECACA", borderRadius: 8, padding: "12px 16px", color: COLORS.red, fontSize: 13, marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>{error}</span>
            <button onClick={() => setError("")} style={{ background: "none", border: "none", color: COLORS.red, cursor: "pointer", fontSize: 16 }}>✕</button>
          </div>
        )}

        {loading ? (
          <div style={{ color: COLORS.primary, fontSize: 13, fontWeight: 500 }}>
            <div style={{ marginBottom: 10 }}>{statusMsg || "Fetching transactions..."}</div>
            <div style={{ height: 3, background: COLORS.surface, borderRadius: 4, overflow: "hidden", width: 240, margin: "0 auto" }}>
              <div style={{ height: "100%", background: COLORS.primary, borderRadius: 4, animation: "slide 1.5s infinite ease-in-out", width: "40%" }} />
            </div>
          </div>
        ) : fetching ? (
          <div style={{ color: COLORS.muted, fontSize: 13 }}>Loading merchants...</div>
        ) : merchants.length === 0 ? (
          <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 32, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>📭</div>
            <div style={{ color: COLORS.textSecondary, fontSize: 14, marginBottom: 8 }}>No merchants connected yet</div>
            <div style={{ color: COLORS.muted, fontSize: 13, lineHeight: 1.7 }}>Send merchants to your onboarding page to connect their bank.</div>
            <div style={{ marginTop: 20, background: COLORS.blueLight, borderRadius: 8, padding: "10px 16px" }}>
              <div style={{ fontSize: 11, color: COLORS.muted, fontWeight: 500, marginBottom: 4 }}>Merchant onboarding link</div>
              <div style={{ fontSize: 13, color: COLORS.primary, fontWeight: 500 }}>connect.starfishadvance.com/connect.html</div>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: "hidden", marginBottom: 16, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
              {merchants.map((m, i) => (
                <div key={m.merchant_id} style={{ borderBottom: i < merchants.length - 1 ? `1px solid ${COLORS.borderLight}` : "none", background: selected === m.merchant_id ? COLORS.blueLight : "transparent", transition: "background 0.15s" }}>
                  {editingId === m.merchant_id ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px" }}>
                      <input autoFocus value={editName} onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") handleRename(m.merchant_id); if (e.key === "Escape") setEditingId(null); }}
                        style={{ flex: 1, background: COLORS.bg, border: `1px solid ${COLORS.primary}`, borderRadius: 6, padding: "6px 10px", fontSize: 13, color: COLORS.text, outline: "none", fontFamily: FONT }} />
                      <button onClick={() => handleRename(m.merchant_id)} disabled={actionLoading}
                        style={{ background: COLORS.primary, border: "none", borderRadius: 6, padding: "6px 14px", fontSize: 12, color: "#fff", cursor: "pointer", fontFamily: FONT, fontWeight: 500 }}>Save</button>
                      <button onClick={() => setEditingId(null)}
                        style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "6px 14px", fontSize: 12, color: COLORS.textSecondary, cursor: "pointer", fontFamily: FONT }}>Cancel</button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px" }}>
                      <div onClick={() => setSelected(m.merchant_id)} style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer", flex: 1 }}>
                        <div style={{ width: 36, height: 36, background: selected === m.merchant_id ? COLORS.primary : COLORS.surface, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: selected === m.merchant_id ? "#fff" : COLORS.muted, fontWeight: 600, flexShrink: 0 }}>
                          {m.merchant_name.charAt(0).toUpperCase()}
                        </div>
                        <div style={{ textAlign: "left" }}>
                          <div style={{ fontSize: 14, color: selected === m.merchant_id ? COLORS.primary : COLORS.text, fontWeight: 600 }}>{m.merchant_name}</div>
                          <div style={{ fontSize: 12, color: COLORS.dim, marginTop: 2 }}>Connected {new Date(m.connected_at).toLocaleDateString()}</div>
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        {selected === m.merchant_id && <span style={{ fontSize: 18, color: COLORS.primary, marginRight: 8 }}>✓</span>}
                        <button onClick={(e) => { e.stopPropagation(); setEditingId(m.merchant_id); setEditName(m.merchant_name); }}
                          style={{ background: "none", border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "3px 10px", fontSize: 11, color: COLORS.muted, cursor: "pointer", fontFamily: FONT }}>Rename</button>
                        <button onClick={(e) => { e.stopPropagation(); handleDelete(m.merchant_id, m.merchant_name); }} disabled={actionLoading}
                          style={{ background: "none", border: `1px solid ${COLORS.redLight}`, borderRadius: 6, padding: "3px 10px", fontSize: 11, color: COLORS.red, cursor: "pointer", fontFamily: FONT, opacity: actionLoading ? 0.3 : 1 }}>Delete</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <button onClick={() => selected && onSelect(selected)} disabled={!selected}
              style={{ width: "100%", background: selected ? GRADIENT : COLORS.surface, border: "none", borderRadius: 8, padding: 14, fontSize: 14, fontWeight: 600, color: selected ? "#fff" : COLORS.dim, cursor: selected ? "pointer" : "not-allowed", fontFamily: FONT, boxShadow: selected ? "0 2px 8px rgba(37,99,235,0.25)" : "none" }}>
              Load Analysis
            </button>
            <div style={{ marginTop: 20, background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "10px 16px", textAlign: "left" }}>
              <div style={{ fontSize: 11, color: COLORS.dim, fontWeight: 500, marginBottom: 4 }}>Send new merchant this link</div>
              <div style={{ fontSize: 13, color: COLORS.muted }}>connect.starfishadvance.com/connect.html</div>
            </div>
          </div>
        )}
      </div>
      <style>{`@keyframes slide { 0% { transform: translateX(-100%) } 100% { transform: translateX(600%) } }`}</style>
    </div>
  );
}
